import type { AtaStatus } from './enums'
import { addDays, compareWeek, isoWeekOf, shiftWeek, weekNumber, weekRange } from './iso-week'

/**
 * Dia ideal das reuniões semanais (RAS e Q3) e a regra de pendência.
 *
 * Espelho de `supply.meeting_due_state` / `supply.meeting_pendencias`
 * (migration 20261022190000_dia_das_reunioes.sql). Os testes dos dois lados
 * usam os mesmos casos (meeting-due.test.ts e supabase/tests/010).
 */

export const MEETING_TIPOS = ['ras', 'q3'] as const
export type MeetingTipo = (typeof MEETING_TIPOS)[number]

export const MEETING_LABEL: Record<MeetingTipo, string> = { ras: 'RAS', q3: 'Q3' }

/** 0 = domingo … 6 = sábado (como `Date#getDay` e `extract(dow)` do Postgres). */
export const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'] as const

/** Ordem de exibição: a semana ISO começa na segunda. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const

export interface MeetingSchedule {
  readonly tipo: MeetingTipo
  /** Nulo = sem dia ideal (não cobra). */
  readonly dia_semana: number | null
  readonly horario: string | null
  /** Pendência vale a partir da semana desta data. */
  readonly vigente_desde: string
}

/** Reunião concluída = ATA fechada ou enviada. Rascunho não conta. */
export function isMeetingConcluded(status: AtaStatus | null | undefined): boolean {
  return status === 'fechada' || status === 'enviada'
}

/** Data (`YYYY-MM-DD`) do dia ideal dentro da semana ISO (segunda a domingo). */
export function meetingDueDate(semana: string, diaSemana: number): string {
  return addDays(weekRange(semana).monday, (diaSemana + 6) % 7)
}

export type MeetingDueKind = 'ok' | 'hoje' | 'atrasada'

/**
 * Estado da reunião da semana. `today` é o dia civil em America/Sao_Paulo
 * (`todayInSaoPaulo()`): o dia ideal só "passa" à meia-noite de Brasília.
 */
export function meetingDueState(
  semana: string,
  diaSemana: number | null,
  concluida: boolean,
  today: string,
): MeetingDueKind {
  if (diaSemana === null || concluida) return 'ok'
  const due = meetingDueDate(semana, diaSemana)
  if (today > due) return 'atrasada'
  if (today === due) return 'hoje'
  return 'ok'
}

export interface MeetingPendency {
  readonly tipo: MeetingTipo
  readonly semana: string
  readonly dueDate: string
  readonly kind: Exclude<MeetingDueKind, 'ok'>
}

/**
 * Reuniões para hoje ou atrasadas, da semana de `vigente_desde` até hoje —
 * semana anterior não concluída continua aqui até concluir. Mais antigas
 * primeiro. `atas` precisa trazer ao menos as ATAs desse intervalo.
 */
export function meetingPendencies(
  schedules: readonly MeetingSchedule[],
  atas: readonly { readonly tipo: string; readonly semana: string; readonly status: AtaStatus }[],
  today: string,
): MeetingPendency[] {
  const done = new Set(atas.filter((a) => isMeetingConcluded(a.status)).map((a) => `${a.tipo}|${a.semana}`))
  const last = isoWeekOf(today)
  const out: MeetingPendency[] = []
  for (const s of schedules) {
    if (s.dia_semana === null) continue
    for (let w: string = isoWeekOf(s.vigente_desde); compareWeek(w, last) <= 0; w = shiftWeek(w, 1)) {
      const kind = meetingDueState(w, s.dia_semana, done.has(`${s.tipo}|${w}`), today)
      if (kind !== 'ok') out.push({ tipo: s.tipo, semana: w, dueDate: meetingDueDate(w, s.dia_semana), kind })
    }
  }
  return out.sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.tipo.localeCompare(b.tipo))
}

/** "segunda, 19/10" (com o artigo: "na segunda", "no sábado"). */
export function weekdayDate(iso: string, withArticle = false): string {
  const dow = new Date(`${iso}T12:00:00Z`).getUTCDay()
  const [, mo, d] = iso.split('-')
  const name = WEEKDAY_NAMES[dow] ?? ''
  const article = withArticle ? (dow === 0 || dow === 6 ? 'no ' : 'na ') : ''
  return `${article}${name}, ${d}/${mo}`
}

/** Frase do alerta — banner, painel e calendário usam a mesma. */
export function meetingPendencyText(p: MeetingPendency): string {
  const head = `${MEETING_LABEL[p.tipo]} da semana ${weekNumber(p.semana)}`
  return p.kind === 'hoje'
    ? `${head} é hoje (${weekdayDate(p.dueDate)}) — conclua a ATA até o fim do dia`
    : `${head} deveria ter sido concluída ${weekdayDate(p.dueDate, true)}`
}

/** Rota da tela da reunião. */
export function meetingPath(tipo: MeetingTipo, semana: string): string {
  return `${tipo === 'ras' ? '/reuniao-ras' : '/reuniao'}?semana=${semana}`
}
